import { useState } from "react";
import { Check, History, NotebookPen, Wrench } from "lucide-react";
import {
  getPlanPriceLabel,
  PUBLIC_PLANS,
  type BillingInterval,
  type PublicPlan,
} from "@omnicanvas/core";
import { ActionLink, Card } from "@omnicanvas/ui";
import { track } from "../analytics";
import { useMetaTags } from "../components/MetaTags";
import { TrialPromise } from "../components/TrialPromise";
import { tools } from "../tools/tools";

const APP_URL = "https://app.omnicanvasnotes.com";

// Every app-bound link carries utm_content so the delegated cta_click listener
// can report which tier and interval was clicked. The paid tiers previously had
// no utm params at all, which meant a pricing-page click was indistinguishable
// from any other app link in the funnel.
function tierHref(tier: PublicPlan, interval: BillingInterval): string {
  if (tier.key === "enterprise") return "/contact";
  const params = new URLSearchParams({
    utm_source: "marketing",
    utm_medium: "pricing",
    utm_campaign: "pricing_page",
    utm_content: `pricing_${tier.key}_${interval}`,
  });
  if (tier.key === "free") {
    params.set("mode", "try");
    params.set("action", "new-note");
    return `${APP_URL}/?${params.toString()}`;
  }
  params.set("upgrade", tier.key);
  if (interval === "year") params.set("interval", "year");
  return `${APP_URL}/settings?${params.toString()}`;
}

// Free-for-everyone extras surfaced on the pricing page. Each one must stay
// true on the Free plan — this block exists to show what an account costs you
// nothing, so a Pro-only feature must never be listed here.
const EXTRAS = [
  {
    icon: Wrench,
    title: `${tools.length} browser tools, no account`,
    body:
      "Whiteboard, mind map, Eisenhower matrix, Cornell notes, flashcards, and more — each one runs in the browser with nothing to install.",
    href: "/tools",
    linkLabel: "Open the tools \u2192",
  },
  {
    icon: NotebookPen,
    title: "Apple Notes companion",
    body:
      "Keep writing where the ink feels best and use OmniCanvas for the spatial work Apple Notes cannot do.",
    href: "/apple-notes-companion",
    linkLabel: "See how it pairs \u2192",
  },
  {
    icon: History,
    title: "72 hours of undo history",
    body:
      "Every sync saves a restore point, kept for three days on every plan. Recover from a bad edit down to the exact sync.",
    href: "/docs/backups",
    linkLabel: "How backups work \u2192",
  },
] as const;

function tierCta(tier: PublicPlan, interval: BillingInterval): string {
  if (tier.contact) return "Contact sales";
  if (tier.perSeat && tier.minSeats) {
    const seatPrice = interval === "year" ? tier.annual || 0 : tier.monthly || 0;
    return `Set up Team — from $${seatPrice * tier.minSeats}/${interval === "year" ? "year" : "month"}`;
  }
  if (tier.key === "free") return "Start my canvas";
  return "Start my free trial";
}

export function Pricing() {
  useMetaTags({
    title: "OmniCanvas Notes — Free Spatial Canvas App",
    description:
      "30-day free trial of every Pro feature. Free tier forever with unlimited notes, infinite canvas, and cloud sync. No credit card.",
    url: "https://omnicanvasnotes.com/pricing",
  });
  // Annual is the default: it collects a year up front, which is what makes
  // paid acquisition fundable — a monthly plan recovers its CAC over ~7 months,
  // an annual one on day one. Monthly stays one click away.
  const [interval, setInterval] = useState<BillingInterval>("year");
  return (
    <div className="pt-28 pb-20">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 mb-4">
            Stop losing what you said you&apos;d do
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            Every plan turns meetings, clips, and scribbles into next actions you
            actually come back to. The canvas, sync, and search are free forever
            &mdash; paid plans add the transcription and AI hours on top.
          </p>
        </div>

        <TrialPromise className="mb-8" />

        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center bg-slate-100 rounded-full p-1 text-sm font-semibold">
            <button
              onClick={() => {
                track("pricing_interval_changed", { interval: "month" });
                setInterval("month");
              }}
              className={`px-5 py-2 rounded-full transition-colors ${
                interval === "month" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => {
                track("pricing_interval_changed", { interval: "year" });
                setInterval("year");
              }}
              className={`px-5 py-2 rounded-full transition-colors flex items-center gap-2 ${
                interval === "year" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Annual
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                2 MONTHS FREE
              </span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PUBLIC_PLANS.map((tier) => {
            const { price, period, monthlyEquivalent } = getPlanPriceLabel(tier, interval);
            const seatPrice = interval === "year" ? tier.annual || 0 : tier.monthly || 0;
            const minimumTotal = tier.perSeat && tier.minSeats ? seatPrice * tier.minSeats : null;
            const displayPrice = minimumTotal !== null ? `$${minimumTotal}` : price;
            const displayPeriod = minimumTotal !== null
              ? `/${interval === "year" ? "year" : "month"} minimum`
              : period;
            const annualSavings =
              tier.monthly !== null && tier.annual !== null && tier.monthly > 0
                ? tier.monthly * 12 - tier.annual
                : null;
            return (
              <Card
                key={tier.name}
                className={`relative flex h-full flex-col p-7 ${
                  tier.highlighted
                    ? "border-indigo-600 bg-indigo-600 text-white ring-2 ring-indigo-600 shadow-xl shadow-indigo-200"
                    : "border-slate-200 bg-white"
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}

                <h3
                  className={`text-lg font-bold mb-1 ${
                    tier.highlighted ? "text-white" : "text-slate-900"
                  }`}
                >
                  {tier.name}
                </h3>
                <p
                  className={`text-sm mb-6 ${
                    tier.highlighted ? "text-indigo-200" : "text-slate-500"
                  }`}
                >
                  {tier.description}
                </p>

                <div className="mb-6">
                  <span
                    className={`text-4xl font-extrabold ${
                      tier.highlighted ? "text-white" : "text-slate-900"
                    }`}
                  >
                    {displayPrice}
                  </span>
                  <span
                    className={`text-sm ml-1 ${
                      tier.highlighted ? "text-indigo-200" : "text-slate-500"
                    }`}
                  >
                    {displayPeriod}
                  </span>
                  {minimumTotal !== null && (
                    <div className={`mt-1 text-xs ${tier.highlighted ? "text-indigo-200" : "text-slate-500"}`}>
                      {tier.minSeats} seats × {price}{period}
                    </div>
                  )}
                  {monthlyEquivalent && (
                    <div
                      className={`text-xs mt-1 ${
                        tier.highlighted ? "text-indigo-200" : "text-slate-500"
                      }`}
                    >
                      ${monthlyEquivalent}{tier.perSeat ? "/seat" : ""}/month, billed annually
                    </div>
                  )}
                  {interval === "year" && annualSavings !== null && annualSavings > 0 && (
                    <div className={`mt-2 text-xs font-semibold ${tier.highlighted ? "text-emerald-200" : "text-emerald-700"}`}>
                      Save ${annualSavings * (tier.minSeats || 1)}{tier.perSeat ? ` for ${tier.minSeats} seats` : ""} each year
                    </div>
                  )}
                  {tier.minSeats && (
                    <div
                      className={`text-xs mt-1 ${
                        tier.highlighted ? "text-indigo-200" : "text-slate-500"
                      }`}
                    >
                      Minimum {tier.minSeats} seats
                    </div>
                  )}
                </div>

                <ul className="mb-8 flex-1 space-y-3">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 text-sm">
                      <Check
                        size={16}
                        className={`mt-0.5 flex-shrink-0 ${
                          tier.highlighted ? "text-indigo-200" : "text-indigo-600"
                        }`}
                      />
                      <span className={tier.highlighted ? "text-indigo-50" : "text-slate-600"}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <ActionLink
                  href={tierHref(tier, interval)}
                  variant={tier.highlighted ? "secondary" : "primary"}
                  className={`w-full ${
                    tier.highlighted
                      ? "border-white bg-white text-indigo-700 hover:bg-indigo-50"
                      : ""
                  }`}
                >
                  {tierCta(tier, interval)}
                </ActionLink>
              </Card>
            );
          })}
        </div>

        <div className="mx-auto mt-14 max-w-4xl rounded-2xl border border-slate-200 bg-slate-50 p-7">
          <h2 className="text-center text-lg font-bold text-slate-900">
            Included with every plan, including Free
          </h2>
          <p className="mx-auto mt-1 max-w-lg text-center text-sm text-slate-600">
            Work we already built and give away. Nothing here is gated behind an
            upgrade.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {EXTRAS.map((extra) => (
              <div key={extra.title} className="rounded-xl border border-slate-200 bg-white p-5">
                <extra.icon size={18} className="text-indigo-600" aria-hidden="true" />
                <h3 className="mt-3 text-sm font-bold text-slate-900">{extra.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">{extra.body}</p>
                <a
                  href={extra.href}
                  className="mt-3 inline-block text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                >
                  {extra.linkLabel}
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
